import { useAbility } from "@casl/react";
import { useStaff } from "@gymflow/api";
import { AlertProvider } from "@gymflow/common";
import classNames from "classnames";
import { useContext, useMemo } from "react";
import { Switch } from "react-router-dom";

import { NewUserSidebarProvider } from "../components/molecules";
import { TopNavbar } from "../components/Navbars/TopNavbar";
import {
  CancelMembershipSidebarProvider,
  CollectPastDueInvoiceSidebarProvider,
  CreateEditTaskSidebarProvider,
  SendEmailSidebarProvider,
  SendSMSToLeadSidebarProvider,
  SendSMSToUserSidebarProvider,
} from "../components/organisms";
import { ActivitySideBarFormProvider } from "../components/Settings/Activity/ActivitySideBarForm";
import { FacilitySideBarFormProvider } from "../components/Settings/Facility/FacilitySideBarForm";
import { PrepaidMembershipFormSidebarProvider } from "../components/Settings/Membership/PrepaidMembershipFormSidebarProvider";
import { RecurringMembershipFormSidebarProvider } from "../components/Settings/Membership/RecurringMembershipFormSidebarProvider";
import { TrialMembershipFormSidebarProvider } from "../components/Settings/Membership/TrialMembershipFormSidebarProvider";
import { ProductFormSidebarProvider } from "../components/Settings/Product/ProductFormSidebarProvider";
import { CreditPackFormSidebarProvider } from "../components/Settings/Sessions/CreditPackFormSidebarProvider";
import { ShopSidebarSidebarProvider } from "../components/Shop/ShopSidebar/ShopSidebarProvider";
import { Sidebar } from "../components/Sidebar/Sidebar";
import { NoteFormSideBarProvider } from "../components/UserMember/Notes/NoteFormSideBarProvider";
import PaymentsSidebarProvider from "../components/UserMember/UserMemberPayments/components/PaymentsSidebarProvider";
import { getVisibleRoutes } from "../helpers/routes";
import { usePageLayout } from "../hooks";
import { useFeaturebase } from "../hooks/useFeaturebase";
import { AbilityContext } from "../permissions";
import {
  ClubSettingsProvider,
  ModalProvider,
  PageTitleProvider,
  SidebarToggleContext,
  StaffPermissionsProvider,
  YupExtensions,
} from "../providers";
import { AuthenticationContext } from "../providers/authentication/AuthenticationContext";
import { AuthenticationProvider } from "../providers/authentication/AuthenticationProvider";
import { generateStaffRoutes } from "../routes";
import useGymflowModels from "../store";

function Staff() {
  const { api } = useGymflowModels();

  const staffRoutes = useMemo(() => generateStaffRoutes(), []);
  const {
    isSidebarOpen,
    isNavbarVisible,
    getRoutes,
    toggleSidebar,
    closeSidebar,
    showSidebar,
  } = usePageLayout({ routes: staffRoutes });
  const { userId: loggedInId } = useContext(AuthenticationContext);
  const { data: staff } = useStaff({ api, staffId: loggedInId });

  const ability = useAbility(AbilityContext);

  const visibleRoutes = getVisibleRoutes(staffRoutes, ability);

  useFeaturebase();
  return (
    <AlertProvider>
      <NoteFormSideBarProvider>
        <CreateEditTaskSidebarProvider>
          <ModalProvider>
            <SidebarToggleContext.Provider
              value={{
                sidebarOpened: isSidebarOpen,
                toggleSidebar: toggleSidebar,
                hideSidebar: closeSidebar,
                showSidebar: showSidebar,
              }}
            >
              <NewUserSidebarProvider>
                <FacilitySideBarFormProvider>
                  <CreditPackFormSidebarProvider>
                    <ProductFormSidebarProvider>
                      <ActivitySideBarFormProvider>
                        <TrialMembershipFormSidebarProvider>
                          <RecurringMembershipFormSidebarProvider>
                            <PrepaidMembershipFormSidebarProvider>
                              <ShopSidebarSidebarProvider>
                                <CollectPastDueInvoiceSidebarProvider>
                                  <PaymentsSidebarProvider>
                                    <CancelMembershipSidebarProvider>
                                      <SendSMSToLeadSidebarProvider>
                                        <SendSMSToUserSidebarProvider>
                                          <SendEmailSidebarProvider>
                                            <>
                                              <Sidebar
                                                routes={visibleRoutes}
                                                closeSidebar={closeSidebar}
                                                sidebarOpened={isSidebarOpen}
                                                userName={`${staff?.firstName} ${staff?.lastName}`}
                                                userEmail={staff?.email}
                                                avatar={staff?.picture}
                                              />
                                              <div
                                                className={classNames(
                                                  " flex h-full max-h-full w-full flex-col lg:w-[calc(100vw-280px)]",
                                                )}
                                              >
                                                {isNavbarVisible && (
                                                  <TopNavbar
                                                    toggleSidebar={
                                                      toggleSidebar
                                                    }
                                                  />
                                                )}
                                                <Switch>
                                                  {getRoutes(staffRoutes)}
                                                </Switch>
                                              </div>
                                            </>
                                          </SendEmailSidebarProvider>
                                        </SendSMSToUserSidebarProvider>
                                      </SendSMSToLeadSidebarProvider>
                                    </CancelMembershipSidebarProvider>
                                  </PaymentsSidebarProvider>
                                </CollectPastDueInvoiceSidebarProvider>
                              </ShopSidebarSidebarProvider>
                            </PrepaidMembershipFormSidebarProvider>
                          </RecurringMembershipFormSidebarProvider>
                        </TrialMembershipFormSidebarProvider>
                      </ActivitySideBarFormProvider>
                    </ProductFormSidebarProvider>
                  </CreditPackFormSidebarProvider>
                </FacilitySideBarFormProvider>
              </NewUserSidebarProvider>
            </SidebarToggleContext.Provider>
          </ModalProvider>
        </CreateEditTaskSidebarProvider>
      </NoteFormSideBarProvider>
    </AlertProvider>
  );
}

function StaffWithProviders() {
  return (
    <AuthenticationProvider userType="staff">
      <PageTitleProvider>
        <ClubSettingsProvider>
          <StaffPermissionsProvider>
            <YupExtensions>
              <Staff />
            </YupExtensions>
          </StaffPermissionsProvider>
        </ClubSettingsProvider>
      </PageTitleProvider>
    </AuthenticationProvider>
  );
}

export default StaffWithProviders;
